import React, { createContext, useContext, useReducer, useCallback, useRef, useEffect } from 'react';
import { saveAssessmentData, getLatestAssessmentData, getChangeListData ,getLatestChangeListForVerification} from '../../services/apiService';
import { decodedToken } from '../../utils/auth';
const AssessmentContext = createContext();

// Local storage key
const STORAGE_KEY = 'assessment_context_state';

// Initial state structure
const initialState = {
  tabs: {
    TV: {
      completedItems: new Set(),
      selectedChanges: { EE: [], PWR: [], ME: [], SW: [], LCM: [] },
      referenceModels: { EE: '', PWR: '', ME: '', SW: '', LCM: '' },
      changeListId: null,
      changeListData: null,
      dataLoaded: false,
      loading: false,
      dataLoadAttempted: false,
      dataSource: null
    },
    MNT: {
      completedItems: new Set(),
      selectedChanges: { EE: [], PWR: [], ME: [], SW: [], LCM: [] },
      referenceModels: { EE: '', PWR: '', ME: '', SW: '', LCM: '' },
      changeListId: null,
      changeListData: null,
      dataLoaded: false,
      loading: false,
      dataLoadAttempted: false,
      dataSource: null
    },
    PD: {
      completedItems: new Set(),
      selectedChanges: { EE: [], PWR: [], ME: [], SW: [], LCM: [] },
      referenceModels: { EE: '', PWR: '', ME: '', SW: '', LCM: '' },
      changeListId: null,
      changeListData: null,
      dataLoaded: false,
      loading: false,
      dataLoadAttempted: false,
      dataSource: null
    },
    AVA: {
      completedItems: new Set(),
      selectedChanges: { EE: [], PWR: [], ME: [], SW: [], LCM: [] },
      referenceModels: { EE: '', PWR: '', ME: '', SW: '', LCM: '' },
      changeListId: null,
      changeListData: null,
      dataLoaded: false,
      loading: false,
      dataLoadAttempted: false,
      dataSource: null
    }
  },
  currentTab: 'TV',
  currentUserId: null
};

// Helper function to load state from localStorage
const loadStateFromStorage = () => {
  try {
    const serializedState = localStorage.getItem(STORAGE_KEY);
    if (serializedState === null) {
      return initialState;
    }
    
    const parsed = JSON.parse(serializedState);
    
    // Convert completedItems arrays back to Sets
    Object.keys(parsed.tabs).forEach(tab => {
      if (Array.isArray(parsed.tabs[tab].completedItems)) {
        parsed.tabs[tab].completedItems = new Set(parsed.tabs[tab].completedItems);
      }
    });
    
    return parsed;
  } catch (err) {
    console.error('Error loading state from localStorage:', err);
    return initialState;
  }
};

// Helper function to save state to localStorage
const saveStateToStorage = (state) => {
  try {
    // Convert Sets to arrays for JSON serialization
    const serializable = {
      ...state,
      tabs: Object.keys(state.tabs).reduce((acc, tab) => {
        acc[tab] = {
          ...state.tabs[tab],
          completedItems: Array.from(state.tabs[tab].completedItems)
        };
        return acc;
      }, {})
    };
    
    const serializedState = JSON.stringify(serializable);
    localStorage.setItem(STORAGE_KEY, serializedState);
  } catch (err) {
    console.error('Error saving state to localStorage:', err);
  }
};

// Action types
const ACTIONS = {
  SET_CURRENT_TAB: 'SET_CURRENT_TAB',
  SET_USER_ID: 'SET_USER_ID',
  SET_CHANGE_LIST: 'SET_CHANGE_LIST',
  SET_LOADING: 'SET_LOADING',
  SET_DATA_LOAD_ATTEMPTED: 'SET_DATA_LOAD_ATTEMPTED',
  SET_SELECTED_CHANGES: 'SET_SELECTED_CHANGES',
  SET_REFERENCE_MODEL: 'SET_REFERENCE_MODEL',
  SET_COMPLETED_ITEM: 'SET_COMPLETED_ITEM',
  REMOVE_COMPLETED_ITEM: 'REMOVE_COMPLETED_ITEM',
  UPDATE_TAB_DATA: 'UPDATE_TAB_DATA',
  CLEAR_TAB_DATA: 'CLEAR_TAB_DATA',
  CLEAR_ALL_DATA: 'CLEAR_ALL_DATA',
  TOGGLE_CHANGE_SELECTION: 'TOGGLE_CHANGE_SELECTION',
  LOAD_SAVED_ASSESSMENT: 'LOAD_SAVED_ASSESSMENT',
  SET_DATA_SOURCE: 'SET_DATA_SOURCE'
};

// Reducer function
const assessmentReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_CURRENT_TAB:
      return {
        ...state,
        currentTab: action.payload
      };

    case ACTIONS.SET_USER_ID:
      return {
        ...state,
        currentUserId: action.payload
      };

    case ACTIONS.SET_LOADING:
      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...state.tabs[action.payload.tab],
            loading: action.payload.loading
          }
        }
      };

    case ACTIONS.SET_DATA_LOAD_ATTEMPTED:
      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...state.tabs[action.payload.tab],
            dataLoadAttempted: action.payload.attempted
          }
        }
      };

    case ACTIONS.SET_DATA_SOURCE:
      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...state.tabs[action.payload.tab],
            dataSource: action.payload.source
          }
        }
      };

    case ACTIONS.SET_CHANGE_LIST:
      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...state.tabs[action.payload.tab],
            changeListId: action.payload.changeListId,
            changeListData: action.payload.changeListData,
            dataLoaded: action.payload.changeListData !== null,
            loading: false,
            dataLoadAttempted: true,
            dataSource: action.payload.dataSource || 'imported',
            selectedChanges: action.payload.preSelectedChanges ? {
              ...state.tabs[action.payload.tab].selectedChanges,
              ...action.payload.preSelectedChanges
            } : state.tabs[action.payload.tab].selectedChanges
          }
        }
      };

    case ACTIONS.LOAD_SAVED_ASSESSMENT:
      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...action.payload.data,
            loading: false,
            dataLoadAttempted: true,
            dataSource: 'saved'
          }
        }
      };

    case ACTIONS.SET_SELECTED_CHANGES:
      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...state.tabs[action.payload.tab],
            selectedChanges: {
              ...state.tabs[action.payload.tab].selectedChanges,
              ...action.payload.selectedChanges
            }
          }
        }
      };

    case ACTIONS.SET_REFERENCE_MODEL:
      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...state.tabs[action.payload.tab],
            referenceModels: {
              ...state.tabs[action.payload.tab].referenceModels,
              [action.payload.item]: action.payload.value
            }
          }
        }
      };

    case ACTIONS.SET_COMPLETED_ITEM:
      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...state.tabs[action.payload.tab],
            completedItems: new Set([...state.tabs[action.payload.tab].completedItems, action.payload.item])
          }
        }
      };

    case ACTIONS.REMOVE_COMPLETED_ITEM:
      const newCompletedItems = new Set(state.tabs[action.payload.tab].completedItems);
      newCompletedItems.delete(action.payload.item);
      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...state.tabs[action.payload.tab],
            completedItems: newCompletedItems
          }
        }
      };

    case ACTIONS.TOGGLE_CHANGE_SELECTION:
      const { tab, item, changeId, changeDetail, isSelected } = action.payload;
      const currentSelectedChanges = state.tabs[tab].selectedChanges[item] || [];
      
      let updatedChanges;
      if (isSelected) {
        const existingIndex = currentSelectedChanges.findIndex(change => change.id === changeId);
        if (existingIndex === -1) {
          updatedChanges = [...currentSelectedChanges, { id: changeId, detail: changeDetail, applied: true }];
        } else {
          updatedChanges = currentSelectedChanges.map((change, index) =>
            index === existingIndex ? { ...change, applied: true } : change
          );
        }
      } else {
        updatedChanges = currentSelectedChanges.filter(change => change.id !== changeId);
      }

      console.log(`Toggle ${changeId} in ${tab}/${item}: ${isSelected ? 'selected' : 'unselected'}`);
      console.log('Updated changes:', updatedChanges);

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [tab]: {
            ...state.tabs[tab],
            selectedChanges: {
              ...state.tabs[tab].selectedChanges,
              [item]: updatedChanges
            }
          }
        }
      };

    case ACTIONS.UPDATE_TAB_DATA:
      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            ...state.tabs[action.payload.tab],
            ...action.payload.data
          }
        }
      };

    case ACTIONS.CLEAR_TAB_DATA:
      return {
        ...state,
        tabs: {
          ...state.tabs,
          [action.payload.tab]: {
            completedItems: new Set(),
            selectedChanges: { EE: [], PWR: [], ME: [], SW: [], LCM: [] },
            referenceModels: { EE: '', PWR: '', ME: '', SW: '', LCM: '' },
            changeListId: null,
            changeListData: null,
            dataLoaded: false,
            loading: false,
            dataLoadAttempted: false,
            dataSource: null
          }
        }
      };

    case ACTIONS.CLEAR_ALL_DATA:
      return {
        ...initialState,
        currentTab: state.currentTab,
        currentUserId: state.currentUserId
      };

    default:
      return state;
  }
};

// Context Provider Component
export const AssessmentProvider = ({ children }) => {
  // Initialize state from localStorage
  const [state, dispatch] = useReducer(assessmentReducer, initialState, loadStateFromStorage);
  const loadingTimeouts = useRef(new Map());

  // Save state to localStorage whenever it changes
  useEffect(() => {
    saveStateToStorage(state);
  }, [state]);

  // Action creators
  const setCurrentTab = useCallback((tab) => {
    dispatch({ type: ACTIONS.SET_CURRENT_TAB, payload: tab });
  }, []);

  const setUserId = useCallback((userId) => {
    dispatch({ type: ACTIONS.SET_USER_ID, payload: userId });
  }, []);

  const setLoading = useCallback((tab, loading) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { tab, loading } });
  }, []);

  const setDataLoadAttempted = useCallback((tab, attempted) => {
    dispatch({ type: ACTIONS.SET_DATA_LOAD_ATTEMPTED, payload: { tab, attempted } });
  }, []);

  const setDataSource = useCallback((tab, source) => {
    dispatch({ type: ACTIONS.SET_DATA_SOURCE, payload: { tab, source } });
  }, []);

  const setChangeList = useCallback((tab, changeListId, changeListData, preSelectedChanges = null, dataSource = 'imported') => {
    dispatch({ 
      type: ACTIONS.SET_CHANGE_LIST, 
      payload: { tab, changeListId, changeListData, preSelectedChanges, dataSource }
    });
  }, []);

  const setSelectedChanges = useCallback((tab, selectedChanges) => {
    dispatch({ 
      type: ACTIONS.SET_SELECTED_CHANGES, 
      payload: { tab, selectedChanges }
    });
  }, []);

  const setReferenceModel = useCallback((tab, item, value) => {
    dispatch({ 
      type: ACTIONS.SET_REFERENCE_MODEL, 
      payload: { tab, item, value }
    });
  }, []);

  const setCompletedItem = useCallback((tab, item) => {
    dispatch({ 
      type: ACTIONS.SET_COMPLETED_ITEM, 
      payload: { tab, item }
    });
  }, []);

  const removeCompletedItem = useCallback((tab, item) => {
    dispatch({ 
      type: ACTIONS.REMOVE_COMPLETED_ITEM, 
      payload: { tab, item }
    });
  }, []);

  const toggleChangeSelection = useCallback((tab, item, changeId, changeDetail, isSelected) => {
    dispatch({ 
      type: ACTIONS.TOGGLE_CHANGE_SELECTION, 
      payload: { tab, item, changeId, changeDetail, isSelected }
    });
  }, []);

  const updateTabData = useCallback((tab, data) => {
    dispatch({ 
      type: ACTIONS.UPDATE_TAB_DATA, 
      payload: { tab, data }
    });
  }, []);

  const clearTabData = useCallback((tab) => {
    if (loadingTimeouts.current.has(tab)) {
      clearTimeout(loadingTimeouts.current.get(tab));
      loadingTimeouts.current.delete(tab);
    }
    
    dispatch({ 
      type: ACTIONS.CLEAR_TAB_DATA, 
      payload: { tab }
    });
  }, []);

  const clearAllData = useCallback(() => {
    loadingTimeouts.current.forEach(timeout => clearTimeout(timeout));
    loadingTimeouts.current.clear();
    
    dispatch({ type: ACTIONS.CLEAR_ALL_DATA });
  }, []);

  const loadSavedAssessmentData = useCallback(async (userId, tab) => {
    try {
      console.log(`Checking for saved assessment data for user: ${userId}, tab: ${tab}`);
      
      const response = await getLatestAssessmentData(userId, tab);
      
      console.log('Response from getLatestAssessmentData:', response);
      
      if (response && response.success === true && response.data) {
        console.log(`✅ Found saved assessment data for ${tab}, loading complete data...`);
        setLoading(tab, true);
        
        const savedData = response.data;
        
        const completedItemsSet = new Set(savedData.completedItems || []);
        
        const changeListId = savedData.changeListId ? String(savedData.changeListId._id || savedData.changeListId) : null;
        
        let completeChangeListData = null;
        
        if (savedData.completeChangeListData) {
          completeChangeListData = savedData.completeChangeListData;
          console.log(`Using complete saved data for ${tab}`);
        } else {
          if (changeListId && /^[0-9a-fA-F]{24}$/.test(changeListId)) {
            try {
              const originalData = await getChangeListData(changeListId);
              completeChangeListData = originalData;
              console.log(`Loaded original change list data for ${tab} as fallback`);
            } catch (error) {
              console.error(`Failed to load fallback data for ${tab}:`, error);
              completeChangeListData = {};
            }
          } else {
            console.warn(`No valid changeListId for fallback in ${tab}`);
            completeChangeListData = {};
          }
        }
        
        const loadedData = {
          changeListId: changeListId,
          selectedChanges: savedData.selectedChanges,
          referenceModels: savedData.referenceModels,
          completedItems: completedItemsSet,
          changeListData: completeChangeListData,
          dataLoaded: completeChangeListData !== null,
          completeChangeListData: savedData.completeChangeListData || completeChangeListData
        };
        
        dispatch({ 
          type: ACTIONS.LOAD_SAVED_ASSESSMENT, 
          payload: { tab, data: loadedData }
        });
        
        console.log(`✅ Successfully loaded complete saved assessment data for ${tab}`);
        return savedData;
      } 
      else if (response && response.success === false) {
        console.log(`ℹ️  No saved assessment data found for ${tab}: ${response.message}`);
        setDataLoadAttempted(tab, true);
        return null;
      }
      else {
        console.warn(`⚠️  Unexpected response format for ${tab}:`, response);
        setDataLoadAttempted(tab, true);
        return null;
      }
    } catch (error) {
      console.error(`❌ Error checking saved assessment data for ${tab}:`, error.message);
      setDataLoadAttempted(tab, true);
      return null;
    }
  }, [setLoading, setDataLoadAttempted]);

const loadChangeListData = useCallback(async (changeListId, tab) => {
  try {
    console.log(`Loading change list data for ${tab}...`);
    setLoading(tab, true);
    
    let data;
    let metadata;
    
    if (changeListId) {
      // Direct fetch by ID (used when user selects from import list)
      console.log(`Fetching specific change list by ID: ${changeListId}`);
      const response = await getChangeListData(changeListId);
      data = response;
      metadata = { source: 'direct', id: changeListId };
    } else {
      // Fetch with priority logic (User upload > PowerAdmin template)
      const decoded = decodedToken();
      const userEmail = decoded?.email;
      const userType = decoded?.userType || 'Admin'; // Get userType from JWT
      
      if (!userEmail || !userType) {
        throw new Error('User email and type not found. Please log in again.');
      }
      
      console.log(`Fetching latest change list for ${tab} with priority logic for user: ${userEmail} (${userType})`);
      
      // Call the new API endpoint that handles priority
      const result = await getLatestChangeListForVerification(tab, userEmail, userType);
      
      if (result.success) {
        data = result.data;
        metadata = result.metadata;
        changeListId = metadata.id;
        
        console.log(`✓ Loaded ${tab} change list from ${metadata.source}: ${metadata.filename}`);
      } else {
        throw new Error(result.message || 'No change list available');
      }
    }
    
    // Pre-select changes with "YES" applied status
    const assessmentItems = ['EE', 'PWR', 'ME', 'SW', 'LCM'];
    const preSelectedChanges = {};
    
    assessmentItems.forEach(item => {
      const sectionData = data[item] || [];
      const preSelected = sectionData
        .filter(row => row.applied === 'YES')
        .map(row => ({
          id: row.id,
          detail: row.detail,
          applied: true
        }));
      
      if (preSelected.length > 0) {
        preSelectedChanges[item] = preSelected;
      }
    });
    
    setChangeList(tab, changeListId, data, preSelectedChanges, metadata.source);
    console.log(`✓ Successfully loaded change list data for ${tab}`);
    
    return data;
  } catch (error) {
    console.error(`Failed to load change list data for ${tab}:`, error);
    setLoading(tab, false);
    setDataLoadAttempted(tab, true);
    throw error;
  }
}, [setLoading, setChangeList, setDataLoadAttempted]);


  // Selectors
  const getCurrentTabData = useCallback(() => {
    return state.tabs[state.currentTab];
  }, [state.tabs, state.currentTab]);

  const getTabData = useCallback((tab) => {
    return state.tabs[tab];
  }, [state.tabs]);

  const isAllCompleted = useCallback((tab) => {
    const assessmentItems = ['EE', 'PWR', 'ME', 'SW', 'LCM'];
    return assessmentItems.every(item => state.tabs[tab].completedItems.has(item));
  }, [state.tabs]);

  const hasChangeList = useCallback((tab) => {
    return state.tabs[tab].changeListId !== null;
  }, [state.tabs]);

  const isDataLoaded = useCallback((tab) => {
    return state.tabs[tab].dataLoaded;
  }, [state.tabs]);

  const isLoading = useCallback((tab) => {
    return state.tabs[tab].loading;
  }, [state.tabs]);

  const hasDataLoadAttempted = useCallback((tab) => {
    return state.tabs[tab].dataLoadAttempted;
  }, [state.tabs]);

  const getDataSource = useCallback((tab) => {
    return state.tabs[tab].dataSource;
  }, [state.tabs]);

  const value = {
    ...state,
    setCurrentTab,
    setUserId,
    setLoading,
    setDataLoadAttempted,
    setDataSource,
    setChangeList,
    setSelectedChanges,
    setReferenceModel,
    setCompletedItem,
    removeCompletedItem,
    toggleChangeSelection,
    updateTabData,
    clearTabData,
    clearAllData,
    loadSavedAssessmentData,
    loadChangeListData,
    getCurrentTabData,
    getTabData,
    isAllCompleted,
    hasChangeList,
    isDataLoaded,
    isLoading,
    hasDataLoadAttempted,
    getDataSource
  };

  return (
    <AssessmentContext.Provider value={value}>
      {children}
    </AssessmentContext.Provider>
  );
};

export const useAssessment = () => {
  const context = useContext(AssessmentContext);
  if (!context) {
    throw new Error('useAssessment must be used within an AssessmentProvider');
  }
  return context;
};

export default AssessmentContext;
